import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight,
  CheckCircle,
  Download,
  FileText,
  Monitor,
  Database,
  BarChart2,
  Cpu,
  Globe,
  Zap,
  ArrowDown,
  TrendingUp,
  Lock,
  ArrowUp,
  Star,
  Settings,
  Eye
} from 'lucide-react';

// Layout components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Container from './components/common/Container';

const Industry4Landing = () => {
  // State for form inputs and validation
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    role: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  // References for sections
  const reportFormRef = useRef(null);
  const insightsRef = useRef(null);
  
  // Initialize on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Monitor scroll position with throttling
  useEffect(() => {
    let timeoutId = null;
    const handleScroll = () => {
      if (!timeoutId) {
        timeoutId = setTimeout(() => {
          setScrolled(window.scrollY > 50);
          timeoutId = null;
        }, 100); // Throttle to 100ms
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);
  
  // Key Industry 4.0 applications and use cases
  const industryUseCases = [
    {
      icon: <Globe className="w-8 h-8 text-white" />,
      title: "Digital Twins",
      description: "How manufacturers are creating virtual replicas of production systems to simulate changes, optimize processes, and troubleshoot issues without disrupting actual operations.",
      stat: "40% faster problem resolution"
    },
    {
      icon: <Cpu className="w-8 h-8 text-white" />,
      title: "Predictive Maintenance",
      description: "Real examples of combining sensor data and machine learning to anticipate equipment failures, allowing for planned maintenance during scheduled downtime.",
      stat: "67% reduction in unplanned downtime"
    },
    {
      icon: <Monitor className="w-8 h-8 text-white" />,
      title: "Connected Worker Solutions",
      description: "How digital tools and wearable technology are enabling frontline workers to access real-time information, follow digital work instructions, and collaborate remotely.",
      stat: "Increased throughput and quality"
    },
    {
      icon: <Settings className="w-8 h-8 text-white" />,
      title: "Autonomous Material Handling",
      description: "Case studies of manufacturers using AGVs and automated systems to optimize material flow, reduce travel time, and improve workplace safety.",
      stat: "23% improvement in material flow"
    }
  ];
  
  // Manufacturing benefits
  const manufacturingBenefits = [
    {
      area: "Product Quality",
      insight: "How real-time monitoring and data analytics are helping manufacturers achieve near-zero defect production and reduce scrap rates."
    },
    {
      area: "Operational Flexibility",
      insight: "Examples of smart factory implementations that enable faster changeovers and more responsive manufacturing."
    },
    {
      area: "Production Visibility",
      insight: "Success stories of manufacturing teams gaining full visibility into operations across multiple production lines and facilities."
    },
    {
      area: "Workforce Development",
      insight: "How Industry 4.0 technologies are creating new opportunities for workforce skill development and engagement."
    }
  ];
  
  // Whitepaper sections
  const reportSections = [
    {
      title: "Industry 4.0 Use Cases",
      description: "Practical applications from different manufacturing sectors showing how technology addresses specific production challenges.",
      icon: <CheckCircle className="w-5 h-5" />
    },
    {
      title: "Smart Factory Roadmaps",
      description: "Step-by-step approaches that real manufacturers have used to transform their operations without disrupting production.",
      icon: <Settings className="w-5 h-5" />
    },
    {
      title: "Technology Integration Examples",
      description: "How companies are connecting new Industry 4.0 systems with existing equipment and production infrastructure.",
      icon: <Cpu className="w-5 h-5" />
    },
    {
      title: "Implementation Lessons",
      description: "Key insights and takeaways from manufacturers who have successfully deployed Industry 4.0 solutions.",
      icon: <Eye className="w-5 h-5" />
    },
    {
      title: "Future Manufacturing Trends",
      description: "Emerging technologies and approaches that are showing promise in early adopter manufacturing environments.",
      icon: <TrendingUp className="w-5 h-5" />
    }
  ];
  
  // FAQs about the whitepaper
  const faqs = [
    {
      question: "How applicable are these examples to our specific manufacturing environment?",
      answer: "The report includes use cases from diverse manufacturing environments including discrete, process, and batch manufacturing across industries like automotive, aerospace, electronics, consumer goods, food & beverage, and pharmaceuticals. We specifically highlight the context of each implementation so you can identify the most relevant examples."
    },
    {
      question: "Will this help us develop our Industry 4.0 roadmap?",
      answer: "Yes. The report includes multiple roadmap examples showing how manufacturers prioritized their implementations. These examples illustrate different starting points and progression paths that can be adapted to your specific manufacturing environment and objectives."
    },
    {
      question: "Do you address connectivity with legacy equipment?",
      answer: "Absolutely. Several of our use cases focus specifically on brownfield implementations where manufacturers connected older equipment using retrofitted sensors, edge devices, and other approaches to bring valuable production data into their Industry 4.0 ecosystem."
    },
    {
      question: "How technical is this report?",
      answer: "The report is designed to be valuable for both operational and technical teams. We explain technologies clearly without unnecessary jargon, while still providing enough technical detail to understand implementation requirements. Each section includes both business context and technical considerations."
    },
    {
      question: "Does the report include actual results and outcomes?",
      answer: "Yes. Each case study includes specific improvements manufacturers achieved in areas like throughput, quality, OEE, changeover time, and other key metrics. We focus on operational improvements rather than just financial returns to provide a comprehensive view of the impact."
    }
  ];
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  // Validate form data
  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }
    
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email address is invalid";
    }
    
    if (!formData.company.trim()) {
      errors.company = "Company name is required";
    }
    
    if (!formData.role.trim()) {
      errors.role = "Job role is required";
    }
    
    return errors;
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm();
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    // Submit form
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // In a real implementation, you would make an API call here
      console.log("Form submitted:", formData);
      
      // Reset form after submission
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        role: ''
      });
    }, 1500);
  };
  
  // Scroll to form
  const scrollToForm = () => {
    reportFormRef.current.scrollIntoView({ behavior: 'smooth' });
  };
  
  // Scroll to insights
  const scrollToInsights = () => {
    insightsRef.current.scrollIntoView({ behavior: 'smooth' });
  };
  
  return (
    <>
      <Helmet>
        <title>Industry 4.0 in Action: Use Cases & Implementation Insights | Smart Factory Report</title>
        <meta name="description" content="Download our report showcasing real-world Industry 4.0 applications, digital twin use cases, and practical smart factory implementation insights from manufacturers." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      </Helmet>

      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-20 lg:pt-28 pb-16 lg:pb-28 overflow-hidden bg-gradient-to-b from-slate-900 via-blue-900 to-cyan-900">
        {/* Background pattern */}
        <div className="absolute inset-0 z-0">
          {/* Use a pattern of dots instead of an image */}
          <div className="absolute inset-0 bg-[radial-gradient(#ffffff33_1px,transparent_1px)] bg-[length:20px_20px]"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-cyan-900"></div>
        </div>
        
        <Container className="relative z-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-center">
            <div className="lg:col-span-7 text-white">
              <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-blue-600/30 backdrop-blur-sm border border-blue-500/30 mb-6">
                <span className="w-2 h-2 rounded-full bg-cyan-400 mr-2 animate-pulse"></span>
                <span className="text-cyan-100 text-sm font-medium">INDUSTRY 4.0 IN ACTION</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 leading-tight">
                Smart Factory <span className="text-cyan-400">Use Cases</span> & Implementation Insights
              </h1>
              
              <p className="text-xl text-blue-100 mb-8 max-w-3xl">
                Learn how leading manufacturers are applying digital twins, IoT, and advanced automation to transform production efficiency, quality, and flexibility.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button
                  onClick={scrollToForm}
                  className="inline-flex items-center justify-center bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-4 rounded-lg font-medium shadow-lg hover:shadow-cyan-500/20 transition-all duration-300 transform hover:-translate-y-1"
                >
                  <Download className="mr-2 w-5 h-5" />
                  <span>Download Report</span>
                </button>
                
                <button
                  onClick={scrollToInsights}
                  className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-6 py-4 rounded-lg font-medium border border-white/20 transition-all duration-300 transform hover:-translate-y-1"
                >
                  <span>Explore Use Cases</span>
                  <ArrowDown className="ml-2 w-5 h-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/10">
                  <div className="text-2xl font-bold text-cyan-400">15+</div>
                  <div className="text-blue-100 text-sm">Use Cases</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/10">
                  <div className="text-2xl font-bold text-cyan-400">7</div>
                  <div className="text-blue-100 text-sm">Industries</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/10">
                  <div className="text-2xl font-bold text-cyan-400">Digital</div>
                  <div className="text-blue-100 text-sm">Twins</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/10">
                  <div className="text-2xl font-bold text-cyan-400">IIoT</div>
                  <div className="text-blue-100 text-sm">Solutions</div>
                </div>
              </div>
            </div>
            
            {/* Report Preview */}
            <div className="lg:col-span-5">
              <div className="relative">
                {/* Background decorative elements */}
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-cyan-400/20 to-blue-500/10 rounded-full blur-xl"></div>
                <div className="absolute -bottom-5 -left-5 w-24 h-24 bg-gradient-to-tr from-blue-500/20 to-cyan-400/10 rounded-full blur-xl"></div>
                
                {/* Report cover */}
                <div className="relative bg-white rounded-lg overflow-hidden shadow-2xl transform rotate-2 hover:rotate-0 transition-all duration-500">
                  <div className="bg-gradient-to-br from-blue-600 to-cyan-500 aspect-[3/4] flex items-center justify-center">
                    <div className="text-center text-white px-4">
                      <Settings className="w-16 h-16 mx-auto mb-4" />
                      <h3 className="text-2xl font-bold mb-2">Industry 4.0 in Action</h3>
                      <p className="text-sm mb-4 text-blue-100">Use Cases & Implementation Insights</p>
                    </div>
                  </div>
                </div>
                
                {/* Featured badge */}
                <div className="absolute -top-5 -right-5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg transform rotate-12">
                  New Edition
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* What's Inside the Report Section */}
      <section className="py-16 lg:py-24 bg-white">
        <Container>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-cyan-50 mb-4">
              <FileText className="w-4 h-4 text-cyan-700 mr-2" />
              <span className="text-cyan-700 text-sm font-medium">IN THIS REPORT</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Real-World Industry 4.0 Applications
            </h2>
            <p className="text-lg text-slate-600">
              Learn from manufacturers who have successfully implemented smart factory technologies with practical insights you can apply to your operations.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 items-start">
            {/* Left column - Report sections */}
            <div className="md:col-span-2 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-6 sm:p-8 border border-slate-100 shadow-xl">
              <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
                <FileText className="w-5 h-5 text-cyan-700 mr-2" />
                Key Sections Covered
              </h3>
              
              <div className="space-y-5">
                {reportSections.map((section, index) => (
                  <div 
                    key={index}
                    className="flex bg-white p-4 rounded-xl shadow-md border border-slate-100 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                  >
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white mr-4 flex-shrink-0">
                      {section.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">{section.title}</h4>
                      <p className="text-slate-600 text-sm">{section.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100 flex items-start">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 mr-3 flex-shrink-0">
                  <Globe className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-1">Bonus: Digital Twin Implementation Guide</h4>
                  <p className="text-slate-600 text-sm">A practical framework for implementing digital twins in manufacturing environments, from basic process simulation to advanced predictive applications.</p>
                </div>
              </div>
            </div>
            
            {/* Right column - Testimonial and call-to-action */}
            <div className="flex flex-col gap-6">
              {/* Testimonial card */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-slate-200 mr-4"></div>
                  <div>
                    <div className="font-bold text-slate-900">Manufacturing Director</div>
                    <div className="text-slate-500 text-sm">Automotive Components</div>
                  </div>
                </div>
                <p className="text-slate-600 italic mb-4">
                  "This report provided exactly what we needed—practical examples and implementation approaches that actually work in production environments. The digital twin case studies were particularly valuable for our connected factory initiative."
                </p>
                {/* Rating stars */}
                <div className="flex text-yellow-400">
                  <Star className="w-5 h-5 fill-current" />
                  <Star className="w-5 h-5 fill-current" />
                  <Star className="w-5 h-5 fill-current" />
                  <Star className="w-5 h-5 fill-current" />
                  <Star className="w-5 h-5 fill-current" />
                </div>
              </div>
              
              {/* Report specs card */}
              <div className="bg-gradient-to-br from-slate-800 to-blue-900 text-white rounded-2xl p-6 shadow-xl">
                <h3 className="text-xl font-bold mb-4">Report Highlights</h3>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-cyan-400 mr-3" />
                    <span>15+ Detailed Use Cases</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-cyan-400 mr-3" />
                    <span>Implementation Approaches</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-cyan-400 mr-3" />
                    <span>Technology Integration Methods</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-cyan-400 mr-3" />
                    <span>Results & Operational Impacts</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-cyan-400 mr-3" />
                    <span>Downloadable PDF Format</span>
                  </li>
                </ul>
                
                <button
                  onClick={scrollToForm}
                  className="w-full mt-6 inline-flex items-center justify-center bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-5 py-3 rounded-lg font-medium shadow-lg hover:shadow-cyan-500/20 transition-all duration-300"
                >
                  <Download className="mr-2 w-5 h-5" />
                  <span>Download Now</span>
                </button>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* Use Cases Section */}
      <section ref={insightsRef} className="py-16 lg:py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-cyan-50/50 to-transparent"></div>
        
        <Container className="relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-cyan-50 mb-4">
              <Cpu className="w-4 h-4 text-cyan-700 mr-2" />
              <span className="text-cyan-700 text-sm font-medium">APPLICATIONS</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Industry 4.0 Technology In Action
            </h2>
            <p className="text-lg text-slate-600">
              Discover how manufacturers are applying these technologies to solve real production challenges and create competitive advantages.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {industryUseCases.map((useCase, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl overflow-hidden shadow-xl border border-slate-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 group"
              >
                {/* Top colored section */}
                <div className="h-24 bg-gradient-to-r from-cyan-600 to-blue-700 flex items-center justify-center p-6">
                  <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    {useCase.icon}
                  </div>
                </div>
                
                {/* Content section */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{useCase.title}</h3>
                  <p className="text-slate-600 mb-4">{useCase.description}</p>
                  
                  <div className="mt-auto pt-4 border-t border-slate-100">
                    <div className="text-cyan-700 font-bold">{useCase.stat}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Manufacturing Benefits Section */}
          <div className="mt-16 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-6 sm:p-8 border border-slate-100 shadow-lg">
            <div className="grid md:grid-cols-12 gap-6 md:gap-8 items-center">
              <div className="md:col-span-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  Operational Benefits for Manufacturers
                </h3>
                <p className="text-slate-600 mb-6">
                  Our report details how these Industry 4.0 applications deliver tangible improvements in key manufacturing performance areas.
                </p>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  {manufacturingBenefits.map((item, index) => (
                    <div key={index} className="flex items-start">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white mr-3 flex-shrink-0">
                        <CheckCircle className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 mb-1">{item.area}</h4>
                        <p className="text-slate-600 text-sm">{item.insight}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="md:col-span-4">
                <div className="bg-white rounded-xl overflow-hidden shadow-lg border border-slate-200">
                  <div className="bg-gradient-to-r from-cyan-500 to-blue-600 aspect-video flex items-center justify-center">
                    <div className="text-white text-center p-6">
                      <Database className="w-10 h-10 mx-auto mb-2" />
                      <div className="text-xl font-bold">IIoT Applications</div>
                      <p className="text-sm text-blue-100 mt-2">Connected factory implementations</p>
                    </div>
                  </div>
                  <div className="p-4">
                    <h4 className="font-bold text-slate-900 mb-1">Implementation Insights</h4>
                    <p className="text-slate-600 text-sm mb-3">From planning to deployment</p>
                    <button
                      onClick={scrollToForm}
                      className="w-full inline-flex items-center justify-center bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
                    >
                      <Download className="mr-2 w-4 h-4" />
                      <span>Get the Report</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* Download Form Section */}
      <section ref={reportFormRef} className="py-16 lg:py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-cyan-900 text-white relative">
        {/* Background pattern - CSS circuit board pattern */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 opacity-10">
            {/* Horizontal lines */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,transparent_24px,white_1px,transparent_1px)] bg-[size:25px_100%]"></div>
            
            {/* Vertical lines */}
            <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_24px,white_1px,transparent_1px)] bg-[size:100%_25px]"></div>
            
            {/* Dots for circuit board nodes */}
            <div className="absolute inset-0 bg-[radial-gradient(white_1px,transparent_1px)] bg-[size:20px_20px] bg-[position:center]"></div>
            
            {/* Additional circuit-like elements */}
            <div className="absolute inset-0 bg-[radial-gradient(white_2px,transparent_2px)] bg-[size:40px_40px] bg-[position:10px_10px]"></div>
          </div>
        </div>
        
        <Container className="relative z-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left column - Form header */}
            <div className="lg:col-span-5">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Get Your Industry 4.0 Use Case Report
              </h2>
              <p className="text-blue-100 text-lg mb-6">
                Download our collection of real-world smart factory implementations, digital twin applications, and Industry 4.0 success stories.
              </p>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 mb-6">
                <h3 className="font-bold text-white mb-2 flex items-center">
                  <Lock className="w-5 h-5 mr-2" />
                  Privacy Promise
                </h3>
                <p className="text-blue-100 text-sm">
                  We respect your privacy. Your information will never be shared with third parties and you can unsubscribe from our communications at any time.
                </p>
              </div>
              
              <div className="hidden lg:block">
                <div className="flex items-center mb-4">
                  <CheckCircle className="w-5 h-5 text-cyan-400 mr-3" />
                  <span className="text-blue-100">Detailed use cases</span>
                </div>
                <div className="flex items-center mb-4">
                  <CheckCircle className="w-5 h-5 text-cyan-400 mr-3" />
                  <span className="text-blue-100">Implementation approaches</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-cyan-400 mr-3" />
                  <span className="text-blue-100">Practical insights from real deployments</span>
                </div>
              </div>
            </div>
            
            {/* Right column - Form */}
            <div className="lg:col-span-7">
              <div className="bg-white rounded-xl p-6 sm:p-8 shadow-2xl">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">Thank You!</h3>
                    <p className="text-slate-600 mb-6">
                      Your report has been sent to your email. If you don't see it in your inbox within a few minutes, please check your spam folder.
                    </p>
                    <a 
                      href="#" 
                      className="inline-flex items-center text-cyan-700 hover:text-blue-800 font-medium"
                    >
                      <Download className="mr-2 w-5 h-5" />
                      <span>Download Again</span>
                    </a>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-bold text-slate-900 mb-6">Download the Report</h3>
                    
                    <form onSubmit={handleSubmit}>
                      <div className="grid sm:grid-cols-2 gap-4 mb-6">
                        <div>
                          <label htmlFor="firstName" className="block text-sm font-medium text-slate-700 mb-1">
                            First Name*
                          </label>
                          <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 rounded-lg border ${formErrors.firstName ? 'border-red-500' : 'border-slate-300'} focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                            placeholder="Your first name"
                          />
                          {formErrors.firstName && (
                            <p className="mt-1 text-sm text-red-600">{formErrors.firstName}</p>
                          )}
                        </div>
                        <div>
                          <label htmlFor="lastName" className="block text-sm font-medium text-slate-700 mb-1">
                            Last Name*
                          </label>
                          <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 rounded-lg border ${formErrors.lastName ? 'border-red-500' : 'border-slate-300'} focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                            placeholder="Your last name"
                          />
                          {formErrors.lastName && (
                            <p className="mt-1 text-sm text-red-600">{formErrors.lastName}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                          Work Email*
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 rounded-lg border ${formErrors.email ? 'border-red-500' : 'border-slate-300'} focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                          placeholder="Your work email"
                        />
                        {formErrors.email && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                        )}
                      </div>
                      
                      <div className="mb-4">
                        <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-1">
                          Company*
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 rounded-lg border ${formErrors.company ? 'border-red-500' : 'border-slate-300'} focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                          placeholder="Your company"
                        />
                        {formErrors.company && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.company}</p>
                        )}
                      </div>
                      
                      <div className="mb-6">
                        <label htmlFor="role" className="block text-sm font-medium text-slate-700 mb-1">
                          Role*
                        </label>
                        <select
                          id="role"
                          name="role"
                          value={formData.role}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 rounded-lg border ${formErrors.role ? 'border-red-500' : 'border-slate-300'} focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                        >
                          <option value="">Select your role</option>
                          <option value="Operations Leadership">Operations Leadership</option>
                          <option value="Plant Management">Plant Management</option>
                          <option value="Engineering">Engineering</option>
                          <option value="IT/OT">IT/OT</option>
                          <option value="Digital Transformation">Digital Transformation</option>
                          <option value="Continuous Improvement">Continuous Improvement</option>
                          <option value="Technical Services">Technical Services</option>
                          <option value="Other">Other</option>
                        </select>
                        {formErrors.role && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.role}</p>
                        )}
                      </div>
                      
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full inline-flex items-center justify-center bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-4 rounded-lg font-medium shadow-lg hover:shadow-cyan-500/20 transition-all duration-300 disabled:opacity-70"
                      >
                        {isSubmitting ? (
                          <>
                            {/* Loading spinner using CSS */}
                            <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-3"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            <Download className="mr-2 w-5 h-5" />
                            Download Report
                          </>
                        )}
                      </button>
                      
                      <div className="mt-4 text-center text-sm text-slate-500">
                        By submitting, you agree to our <a href="#" className="text-cyan-700 hover:underline">Privacy Policy</a>.
                      </div>
                    </form>
                    
                    <div className="mt-6 pt-6 border-t border-slate-200 lg:hidden">
                      <div className="flex items-center mb-3">
                        <CheckCircle className="w-5 h-5 text-cyan-600 mr-3" />
                        <span className="text-slate-600">Detailed use cases</span>
                      </div>
                      <div className="flex items-center mb-3">
                        <CheckCircle className="w-5 h-5 text-cyan-600 mr-3" />
                        <span className="text-slate-600">Implementation approaches</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-cyan-600 mr-3" />
                        <span className="text-slate-600">Practical insights from real deployments</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-white">
        <Container>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600">
              Common questions about our Industry 4.0 implementation report.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-all duration-300"
              >
                <h3 className="text-lg font-bold text-slate-900 mb-3">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-16 text-center">
            <p className="text-slate-600 mb-6">
              Have other questions about Industry 4.0 implementation or our report?
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="/contact"
                className="inline-flex items-center justify-center bg-white text-cyan-700 border border-cyan-700 px-6 py-3 rounded-lg font-medium hover:bg-cyan-50 transition-colors duration-300"
              >
                <span>Contact Our Team</span>
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <button
                onClick={scrollToForm}
                className="inline-flex items-center justify-center bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-lg font-medium shadow-lg hover:shadow-cyan-500/20 transition-all duration-300"
              >
                <Download className="mr-2 w-5 h-5" />
                <span>Download the Report</span>
              </button>
            </div>
          </div>
        </Container>
      </section>
      
      {/* Final CTA Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-r from-cyan-500 to-blue-600 text-white">
        <Container>
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Discover What's Working in Smart Manufacturing
              </h2>
              <p className="text-blue-100 text-lg mb-8 max-w-3xl">
                Get practical insights from manufacturers who have successfully implemented digital twins, IIoT, and connected worker solutions in real production environments.
              </p>
              
              <div className="flex flex-wrap gap-4 items-center">
                <button
                  onClick={scrollToForm}
                  className="inline-flex items-center justify-center bg-white text-blue-700 px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-all duration-300"
                >
                  <Download className="mr-2 w-5 h-5" />
                  <span>Download the Report</span>
                </button>
                
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span>Complimentary access</span>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-4">
              <div className="p-1 bg-white/20 backdrop-blur-sm rounded-xl">
                <div className="bg-gradient-to-r from-cyan-500 to-blue-600 aspect-video rounded-lg flex items-center justify-center p-6">
                  <div className="text-center text-white">
                    <Cpu className="w-12 h-12 mx-auto mb-2" />
                    <div className="text-xl font-bold">Industry 4.0 in Action</div>
                    <div className="text-sm mt-1">Implementation Use Cases</div>
                    <div className="text-xs text-blue-100 mt-3">
                      From real manufacturing environments
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      <Footer />
      
      {/* Back to top button */}
      {scrolled && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl shadow-lg flex items-center justify-center hover:-translate-y-1 transition-all duration-300 z-20 hover:shadow-xl"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </>
  );
};

export default Industry4Landing;